import { InputHTMLAttributes, useEffect, useState } from "react";

const parse = (t: string) => (t.trim() === "" ? NaN : Number(t.replace(",", ".")));

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type" | "min" | "max"> & {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  integer?: boolean;
};

// A number field that can be emptied while typing. Clamping on every
// keystroke (Math.max(1, Number(v) || 1)) puts the minimum straight back, so
// a lone "1" could never be erased. Here the text is kept as typed and only
// valid numbers are passed on; leaving the field restores the real value.
export default function NumberInput({ value, onChange, min, max, integer, onBlur, ...rest }: Props) {
  const [text, setText] = useState(String(value));
  // Follow outside changes (a reset, a clamp) unless the text already says it
  useEffect(() => { setText((t) => (parse(t) === value ? t : String(value))); }, [value]);

  const clamp = (n: number) => {
    if (integer) n = Math.round(n);
    if (min !== undefined) n = Math.max(min, n);
    if (max !== undefined) n = Math.min(max, n);
    return n;
  };

  return (
    <input
      {...rest}
      type="number"
      min={min}
      max={max}
      value={text}
      onChange={(e) => {
        const v = e.target.value;
        setText(v);
        const n = parse(v);
        if (Number.isFinite(n) && (min === undefined || n >= min)) onChange(clamp(n));
      }}
      onBlur={(e) => { setText(String(value)); onBlur?.(e); }}
    />
  );
}
